<template>
  <div class="profiles-page" dir="rtl">

    <!-- delete confirm modal -->
    <ui-modal v-model="showDeleteModal" title="מחיקת פרופיל" size="sm" :closable="true" @close="showDeleteModal = false">
      <div class="confirm-dialog">
        <div class="confirm-dialog__icon-badge" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
        </div>
        <p class="confirm-dialog__body">האם אתה בטוח שברצונך למחוק את הפרופיל? פעולה זו אינה ניתנת לביטול.</p>
      </div>
      <template slot="footer">
        <ui-button variant="ghost" size="md" @click="showDeleteModal = false">ביטול</ui-button>
        <ui-button variant="danger" size="md" :loading="deleting" @click="confirmDelete">מחק פרופיל</ui-button>
      </template>
    </ui-modal>

    <div class="profiles-page__inner">
      <h1 class="profiles-page__title">ניהול פרופילים</h1>

      <!-- inline error banner -->
      <div v-if="errorMsg" class="profiles-page__banner" role="alert" aria-live="assertive">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        <span>{{ errorMsg }}</span>
      </div>

      <!-- Subscription section -->
      <div class="sub-section">
        <div class="sub-section__status">
          <div class="sub-section__status-row">
            <span class="sub-section__label">סטטוס מנוי</span>
            <span class="sub-section__badge" :class="user.is_active ? 'sub-section__badge--active' : 'sub-section__badge--inactive'">
              {{ user.is_active ? 'פעיל' : 'לא פעיל' }}
            </span>
          </div>
          <div v-if="user.subscription_validity" class="sub-section__status-row">
            <span class="sub-section__label">תוקף</span>
            <span class="sub-section__value">{{ user.subscription_validity }}</span>
          </div>
          <ui-button v-if="!showPlans" variant="primary" size="md" class="sub-section__upgrade-btn" @click="openPlans">שדרג / חדש מנוי</ui-button>
        </div>

        <!-- plan picker -->
        <div v-if="showPlans" class="sub-section__plans">
          <p class="sub-section__plans-title">בחר מסלול</p>
          <div v-if="plansLoading" class="sub-section__loading">טוען מסלולים...</div>
          <div v-else class="sub-section__plans-grid">
            <label
              v-for="plan in plans"
              :key="plan.id"
              class="sub-plan-card"
              :class="{ 'sub-plan-card--selected': selectedPlanId === plan.id }"
            >
              <input type="radio" :value="plan.id" v-model="selectedPlanId" class="sub-plan-card__radio" />
              <span class="sub-plan-card__name">{{ plan.name }}</span>
              <span class="sub-plan-card__price">
                {{ plan.discount_months > 0 ? plan.discount_price : plan.price }} ₪{{ periodLabel(plan) ? ' / ' + periodLabel(plan) : '' }}
              </span>
              <span v-if="plan.discount_months > 0" class="sub-plan-card__note">מחיר מוזל ל-{{ plan.discount_months }} חודשים</span>
              <span v-if="plan.free_days > 0" class="sub-plan-card__note">{{ plan.free_days }} ימי ניסיון חינם</span>
            </label>
          </div>
          <div class="sub-section__plan-actions">
            <ui-button variant="ghost" size="md" @click="showPlans = false">ביטול</ui-button>
            <ui-button variant="primary" size="md" :disabled="!selectedPlanId" :loading="payLoading" @click="startPayment">המשך לתשלום</ui-button>
          </div>
          <p v-if="payError" class="sub-section__error">{{ payError }}</p>
        </div>

        <!-- Tranzila iframe -->
        <div v-if="showPaymentIframe" class="sub-section__iframe-wrap">
          <button type="button" class="sub-section__iframe-close" @click="showPaymentIframe = false" aria-label="סגור">✕</button>
          <payment-tranzila :plan_id="paymentPlanId" :sum="paymentSum" @payment_done="onPaymentDone" />
        </div>

        <!-- success -->
        <div v-if="paymentSuccess" class="sub-section__success">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
          הפרטים עודכנו בהצלחה! המנוי שלך פעיל.
        </div>
      </div>

      <ul class="profiles-page__grid" role="list">

        <!-- existing profiles -->
        <li v-for="item in profiles" :key="'prof' + item.id" class="profile-card">
          <div class="profile-card__avatar" aria-hidden="true">
            <svg class="profile-card__avatar-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
          </div>
          <span class="profile-card__name">{{ item.name }}</span>
          <div class="profile-card__actions">
            <router-link :to="'/set_up/' + item.id" class="profile-card__action-link">עריכה</router-link>
            <button
              v-if="user.is_active"
              type="button"
              class="profile-card__action-link profile-card__action-link--danger"
              @click="askDelete(item.id)"
            >מחיקה</button>
          </div>
        </li>

        <!-- add profile tile -->
        <li v-if="user.is_active" class="profile-card profile-card--add">
          <router-link to="/user/profiles/insert" class="profile-card__add-link" aria-label="הוסף פרופיל">
            <div class="profile-card__avatar profile-card__avatar--add" aria-hidden="true">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </div>
            <span class="profile-card__name">הוסף פרופיל</span>
            <span class="profile-card__sub">ללא הגבלה</span>
          </router-link>
        </li>

      </ul>
    </div>
  </div>
</template>

<script>
import UiModal          from './ui/Modal.vue';
import UiButton         from './ui/Button.vue';
import PaymentTranzila  from './payment_tranzila.vue';

export default {
  components: { UiModal, UiButton, PaymentTranzila },
  data() {
    return {
      profiles: [],
      user: {},
      showDeleteModal: false,
      deleteTargetId: null,
      deleting: false,
      errorMsg: '',
      showPlans: false,
      plans: [],
      plansLoading: false,
      selectedPlanId: null,
      payLoading: false,
      payError: '',
      showPaymentIframe: false,
      paymentPlanId: 0,
      paymentSum: 0,
      paymentSuccess: false,
    };
  },
  methods: {
    // Maps the API's `period` ("monthly" | "yearly") to the Hebrew label used
    // next to the price. Plans without a period (e.g. free) show no suffix.
    periodLabel(plan) {
      // A free plan has no billing cadence to show, regardless of what
      // `period` the DB row carries (ENUM defaults to 'monthly').
      const price = plan && (Number(plan.discount_months) > 0 ? plan.discount_price : plan.price);
      if (!plan || !plan.period || !(Number(price) > 0)) return '';
      if (plan.period === 'yearly') return 'שנה';
      if (plan.period === 'monthly') return 'חודש';
      return '';
    },
    openPlans() {
      this.showPlans = true;
      this.payError = '';
      if (this.plans.length > 0) return;
      this.plansLoading = true;
      this.api_call({
        url: 'get_plans',
        onComplete: (resp) => {
          this.plansLoading = false;
          const body = resp && resp.data && resp.data.data;
          this.plans = (body && (body.data || body.plans || body)) || [];
          if (!Array.isArray(this.plans)) this.plans = [];
        },
      }).catch(() => { this.plansLoading = false; });
    },
    startPayment() {
      if (!this.selectedPlanId) return;
      this.payError = '';
      // create_payment now runs inside <payment-tranzila> on mount; just hand it the plan.
      const plan = this.plans.find((p) => p.id === this.selectedPlanId);
      this.paymentPlanId = this.selectedPlanId;
      this.paymentSum = plan ? (plan.discount_months > 0 ? plan.discount_price : plan.price) : 0;
      this.showPlans = false;
      this.showPaymentIframe = true;
    },
    onPaymentDone() {
      this.showPaymentIframe = false;
      this.paymentSuccess = true;
      this.showPlans = false;
      // Reload user data to reflect new subscription status
      this.api_call({
        url: 'general',
        onComplete: (data) => {
          this.user = data.data.data.user;
        },
      });
    },
    askDelete(id) {
      this.deleteTargetId = id;
      this.errorMsg = '';
      this.showDeleteModal = true;
    },
    confirmDelete() {
      if (!this.deleteTargetId) return;
      this.deleting = true;
      this.api_call({
        url: 'remove_profile',
        data: { id: this.deleteTargetId },
        onComplete: () => {
          this.deleting = false;
          this.showDeleteModal = false;
          document.location = '/user/profiles';
        },
      });
    },
  },
  created() {
    this.api_call({
      url: 'general',
      onComplete: (data) => {
        this.user     = data.data.data.user;
        this.profiles = data.data.data.user.profiles;
        this.$emit('loaded');
      },
    });
  },
};
</script>

<style scoped lang="scss">
.profiles-page {
  min-height: 100vh;
  background: var(--color-bg);
  padding-block: var(--space-12);
  font-family: var(--font-hebrew);
}

.profiles-page__inner {
  max-width: 900px;
  margin-inline: auto;
  padding-inline: var(--space-6);
}

.profiles-page__title {
  font-size: 28px;
  font-weight: 700;
  color: var(--color-text);
  letter-spacing: -0.4px;
  margin-block-end: var(--space-8);
  text-align: start;
}

/* inline error banner */
.profiles-page__banner {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: 14px 20px;
  background: rgba(224,65,65,0.12);
  border: 1px solid rgba(224,65,65,0.33);
  border-radius: var(--radius-md);
  color: var(--color-danger);
  font-size: 14px;
  margin-block-end: var(--space-6);
}

/* grid */
.profiles-page__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: var(--space-6);
  list-style: none;
  padding: 0;
  margin: 0;
}

/* profile card */
.profile-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  text-align: center;
}

.profile-card__avatar {
  width: 140px;
  height: 140px;
  border-radius: var(--radius-md);
  background: var(--color-surface3);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color var(--duration-base) var(--easing-default);
  border: 2px solid transparent;
}

.profile-card__avatar-icon {
  color: var(--color-textMuted);
}

.profile-card__name {
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text);
  margin-block-start: var(--space-2);
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-card__sub {
  font-size: 12px;
  color: var(--color-textMuted);
}

.profile-card__actions {
  display: flex;
  gap: var(--space-3);
  margin-block-start: var(--space-1);
}

.profile-card__action-link {
  font-size: 13px;
  font-weight: 500;
  color: var(--color-textMuted);
  text-decoration: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--space-2) var(--space-3);
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  font-family: var(--font-hebrew);
  transition: color var(--duration-fast) var(--easing-default);
}

.profile-card__action-link:hover {
  color: var(--color-text);
}

.profile-card__action-link--danger:hover {
  color: var(--color-danger);
}

.profile-card__action-link:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
  border-radius: 2px;
}

/* add tile */
.profile-card--add .profile-card__add-link {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  text-decoration: none;
}

.profile-card__avatar--add {
  border: 2px dashed var(--color-border);
  background: transparent;
  transition: border-color var(--duration-base) var(--easing-default),
              background var(--duration-base) var(--easing-default);
}

.profile-card--add:hover .profile-card__avatar--add {
  border-color: var(--color-borderHi);
  background: var(--color-surface2);
}

.profile-card__avatar--add svg {
  color: var(--color-textMuted);
}

.profile-card--add:hover .profile-card__name {
  color: var(--color-text);
}

.profile-card__add-link:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 4px;
  border-radius: var(--radius-sm);
}

/* confirm dialog body */
.confirm-dialog {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-4);
  text-align: center;
}

.confirm-dialog__icon-badge {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(224,65,65,0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-danger);
}

.confirm-dialog__body {
  font-size: 14px;
  color: var(--color-textMuted);
  line-height: 1.5;
  margin: 0;
}

@media (prefers-reduced-motion: reduce) {
  .profile-card__avatar,
  .profile-card__avatar--add,
  .profile-card__action-link {
    transition: none;
  }
}

/* ── Subscription section ── */
.sub-section {
  background: var(--color-surface1, #111);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: var(--radius-md);
  padding: var(--space-6);
  margin-block-end: var(--space-8);
}

.sub-section__status-row {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin-block-end: var(--space-3);
}

.sub-section__label {
  font-size: 14px;
  color: var(--color-textMuted);
  min-inline-size: 80px;
}

.sub-section__value {
  font-size: 14px;
  color: var(--color-text);
}

.sub-section__badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: var(--radius-pill);
  font-size: 12px;
  font-weight: 700;

  &--active   { background: rgba(34,197,94,0.15); color: #4ade80; }
  &--inactive { background: rgba(239,68,68,0.15);  color: #f87171; }
}

.sub-section__upgrade-btn { margin-block-start: var(--space-4); }

.sub-section__plans-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--color-text);
  margin-block: var(--space-4) var(--space-3);
}

.sub-section__loading {
  font-size: 14px;
  color: var(--color-textMuted);
  padding-block: var(--space-4);
}

.sub-section__plans-grid {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  margin-block-end: var(--space-4);
}

.sub-plan-card {
  flex: 1 1 160px;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-4);
  border: 2px solid rgba(255,255,255,0.1);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: border-color 0.15s;

  &--selected { border-color: var(--color-accent); }

  &__radio { display: none; }

  &__name {
    font-size: 15px;
    font-weight: 700;
    color: var(--color-text);
  }

  &__price {
    font-size: 18px;
    font-weight: 700;
    color: var(--color-accent);
  }

  &__note {
    font-size: 12px;
    color: var(--color-textMuted);
  }
}

.sub-section__plan-actions {
  display: flex;
  gap: var(--space-3);
  align-items: center;
}

.sub-section__error {
  font-size: 14px;
  color: #f87171;
  margin-block-start: var(--space-3);
}

.sub-section__iframe-wrap {
  position: relative;
  margin-block-start: var(--space-4);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.sub-section__iframe-close {
  position: absolute;
  top: var(--space-2);
  inset-inline-start: var(--space-2);
  z-index: 2;
  background: rgba(0,0,0,0.6);
  color: #fff;
  border: none;
  border-radius: var(--radius-sm);
  width: 32px;
  height: 32px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sub-section__success {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  background: rgba(34,197,94,0.1);
  border: 1px solid rgba(34,197,94,0.25);
  border-radius: var(--radius-sm);
  color: #4ade80;
  font-size: 15px;
  margin-block-start: var(--space-4);
}
</style>
